import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, ChevronRight, RefreshCw, Loader2, BedDouble, Bath, Maximize } from 'lucide-react';
import { fetchAllProperties, startCleaning, type Property } from '@/services';
import styles from './Properties.module.css';

type LoadingState = 'loading' | 'error' | 'success';

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [startingCleaningId, setStartingCleaningId] = useState<string | null>(null);

  const loadProperties = async () => {
    setLoadingState('loading');
    setError(null);

    const result = await fetchAllProperties();

    if (result.error) {
      setError(result.error.message);
      setLoadingState('error');
    } else {
      setProperties(result.data);
      setLoadingState('success');
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const handlePropertyPress = async (property: Property) => {
    setStartingCleaningId(property.id);

    const result = await startCleaning(property.id);

    if (result.error) {
      setError(result.error.message);
      setStartingCleaningId(null);
      return;
    }

    navigate(`/checklist/${property.id}`, {
      state: {
        cleaningId: result.data.id,
        propertyName: property.name,
        propertyAddress: property.address || '',
        thumbnailUrl: property.thumbnail_url,
        beds: property.beds,
        baths: property.baths,
        sqft: property.sqft,
      },
    });

    setStartingCleaningId(null);
  };

  const renderContent = () => {
    if (loadingState === 'loading') {
      return (
        <div className={styles.centerContainer}>
          <Loader2 size={32} className={styles.spinner} />
          <p className={styles.loadingText}>Loading properties...</p>
        </div>
      );
    }

    if (loadingState === 'error') {
      return (
        <div className={styles.centerContainer}>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryButton} onClick={loadProperties}>
            <RefreshCw size={18} />
            <span>Try Again</span>
          </button>
        </div>
      );
    }

    if (properties.length === 0) {
      return (
        <div className={styles.centerContainer}>
          <Building2 size={48} className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>No Properties</h2>
          <p className={styles.emptyText}>
            No properties have been set up yet.<br />
            Add properties in the Supabase dashboard.
          </p>
        </div>
      );
    }

    return (
      <div className={styles.scrollContent}>
        {properties.map((property) => (
          <button
            key={property.id}
            className={styles.propertyCard}
            onClick={() => handlePropertyPress(property)}
            disabled={startingCleaningId !== null}
          >
            {property.thumbnail_url ? (
              <img 
                src={property.thumbnail_url} 
                alt={property.name}
                className={styles.propertyThumbnail}
              />
            ) : (
              <div className={styles.propertyIcon}>
                <Building2 size={24} />
              </div>
            )}
            <div className={styles.propertyContent}>
              <span className={styles.propertyName}>{property.name}</span>
              {property.address && (
                <div className={styles.addressRow}>
                  <MapPin size={14} />
                  <span className={styles.propertyAddress}>
                    {property.address.split(',').slice(0, 1)}<br />
                    {property.address.split(',').slice(1).join(',').trim()}
                  </span>
                </div>
              )}
              {(property.beds || property.baths || property.sqft) && (
                <div className={styles.statsRow}>
                  {property.beds && (
                    <span className={styles.stat}>
                      <BedDouble size={14} />
                      <span>{property.beds} {property.beds === 1 ? 'bed' : 'beds'}</span>
                    </span>
                  )}
                  {property.baths && (
                    <span className={styles.stat}>
                      <Bath size={14} />
                      <span>{property.baths} {property.baths === 1 ? 'bath' : 'baths'}</span>
                    </span>
                  )}
                  {property.sqft && (
                    <span className={styles.stat}>
                      <Maximize size={14} />
                      <span>{property.sqft.toLocaleString()} ft²</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            {startingCleaningId === property.id ? (
              <Loader2 size={20} className={styles.spinner} />
            ) : (
              <ChevronRight size={20} className={styles.chevron} />
            )}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Select Property</h1>
        <p className={styles.subtitle}>Choose a property to start cleaning</p>
      </header>
      <main className={styles.main}>
        {renderContent()}
      </main>
    </div>
  );
}


